import { MutationCache, QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import i18n from "@/i18n";
import { isToetsWeigering } from "@/lib/toetsWeigering";

/**
 * Niets weigert stil (storingsronde 18-08, deel C): elke mutatie
 * zonder eigen foutafhandeling krijgt hier de zichtbare melding
 * "Opslaan is niet gelukt. Probeer het opnieuw." — plus één stille
 * automatische herkansing bij een netwerkfout. Een 401 hoort hier
 * niet: die loopt de refresh/herlogin-flow in de axios-laag. Een
 * geweigerde actie van een toetsaccount ook niet: die meldt de
 * axios-laag zelf (lib/toetsWeigering.ts).
 */

function statusVan(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}

/** Netwerkfout = geen antwoord ontvangen (verbinding weg, timeout). */
function isNetwerkFout(error: unknown): boolean {
  const e = error as {
    response?: unknown;
    code?: string;
    name?: string;
  };
  if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") {
    return false;
  }
  return Boolean(e && "code" in (e as object) && !e.response);
}

/**
 * Globale TanStack Query client.
 * - 60s default stale time (klantenlijst cached kort)
 * - queries: 3x retry met exponential backoff op netwerk-fouten,
 *   geen retry op 4xx (behalve 401, die refresh triggert in axios)
 * - mutaties: 1 stille herkansing bij een netwerkfout, daarna de
 *   zichtbare foutmelding via de MutationCache hieronder
 */
export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _variabelen, _context, mutation) => {
      // Mutaties met een eigen onError (toast of formulier-melding)
      // melden zelf; dubbelen zou verwarren. Een 401 wordt door de
      // auth-flow afgehandeld (refresh of herlogin) — daar hoort geen
      // opslaan-melding overheen.
      if (mutation.options.onError || mutation.meta?.eigenFoutmelding) {
        return;
      }
      if (statusVan(error) === 401) return;
      // Toetsaccount (sessie 77): de axios-laag toonde al de vaste
      // toets-melding; geen tweede, generieke eroverheen.
      if (isToetsWeigering(error)) return;
      toast.error(i18n.t("algemeen.opslaan_mislukt"));
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
      retry: (failureCount, error: unknown) => {
        if (failureCount >= 3) return false;
        const status = statusVan(error);
        if (status && status >= 400 && status < 500) return false;
        return true;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: (failureCount, error: unknown) =>
        failureCount < 1 && isNetwerkFout(error),
    },
  },
});
